import React from "react";
import { Link } from "react-router-dom";
import {
  Smartphone,
  Gamepad2,
  Rocket,
  Workflow,
  Brain,
  Globe,
  Server,
  Puzzle,
  MessageCircle,
  Check,
  ArrowRight,
} from "lucide-react";
import config from "../config";

const iconMap: Record<string, React.ElementType> = {
  smartphone: Smartphone,
  "gamepad-2": Gamepad2,
  rocket: Rocket,
  workflow: Workflow,
  brain: Brain,
  globe: Globe,
  server: Server,
  puzzle: Puzzle,
  "message-circle": MessageCircle,
};

export default function Services() {
  const consultancy = config.services.find((s) => s.id === "consultancy");
  const otherServices = config.services.filter((s) => s.id !== "consultancy");

  return (
    <div className="pt-[72px]">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-white/5 bg-slate-950 py-20">
        <div className="animate-gradient-bg absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 opacity-60" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h1 className="text-4xl font-extrabold text-white sm:text-5xl">
            Our Services
          </h1>
          <p className="mt-4 text-lg text-slate-400">
            Comprehensive software development solutions tailored to your needs.
          </p>
        </div>
      </section>

      {/* Consultancy CTA */}
      {consultancy && (
        <section className="border-b border-white/5 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 py-12">
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-6 text-center md:flex-row md:text-left">
            <MessageCircle className="shrink-0 text-cyan-400" size={48} />
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white">
                {consultancy.title}
              </h2>
              <p className="mt-1 text-slate-400">{consultancy.description}</p>
            </div>
            <div className="shrink-0 text-center">
              <span className="block text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                {consultancy.price}
              </span>
              <span className="text-sm text-slate-500">
                {consultancy.priceNote}
              </span>
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 px-6 py-2.5 text-sm font-semibold text-white shadow transition-transform hover:scale-105"
              >
                Book Now <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Services Grid (Bento-box) */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {otherServices.map((service) => {
              const Icon = iconMap[service.icon] || Puzzle;
              return (
                <div
                  key={service.id}
                  className="group relative rounded-2xl border border-white/5 bg-slate-900/60 p-8 transition-all duration-300 hover:scale-[1.02] hover:border-cyan-500/40 hover:shadow-xl hover:shadow-cyan-500/5 hover:ring-1 hover:ring-cyan-500/20"
                >
                  {/* Icon */}
                  <div className="mb-5 inline-flex rounded-xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 p-3">
                    <Icon className="text-cyan-400" size={28} />
                  </div>

                  {/* Title + Price */}
                  <h3 className="text-lg font-semibold text-white">
                    {service.title}
                  </h3>
                  <p className="mt-1 text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                    {service.price}
                  </p>
                  {service.priceNote && (
                    <p className="text-xs text-slate-500">{service.priceNote}</p>
                  )}

                  {/* Description */}
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="mt-5 space-y-2">
                    {service.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2 text-sm text-slate-400"
                      >
                        <Check
                          size={14}
                          className="mt-0.5 shrink-0 text-emerald-400"
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-white/5 py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold text-white">
            Need Something Custom?
          </h2>
          <p className="mt-3 text-slate-400">
            Every project is unique. Reach out and let&rsquo;s discuss your
            specific requirements.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 px-8 py-4 font-semibold text-white shadow-lg shadow-cyan-500/20 transition-transform hover:scale-105"
          >
            Contact Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
